import { Injectable, inject } from '@angular/core';
import { SharedService } from '../Shared/shared-service.service';
import { APIAddress } from './apiAddresses';
import { start } from '@popperjs/core';

@Injectable({
	providedIn: 'root'
})
export class ConstructAPI {
	private apiAddress = inject(APIAddress);
	private sharedService = inject(SharedService);

	getChangePassword() {
		return this.apiAddress.CHANGE_PASSWORD;
	}

	getChangeCompanyUserRole(companyId: number, username: string) {
		return this.apiAddress.COMPANY + '/' + companyId + '/user/' + username + '/role';
	}

	getAllRoles() {
		return this.apiAddress.ALL_ROLES;
	}

	getAllLoans(ids?: number[], includeShared?: boolean) {
		const address = this.apiAddress.ALL_LOANS;
		const p: Record<string, any> = {};

		if (includeShared != undefined) p.inclShared = includeShared;
		const params = new URLSearchParams(p);
		if (ids?.length > 0) ids.forEach(s => params.append('id', s.toString()));

		return params.toString().length > 0 ? `${address}?${params.toString()}` : address;
	}

	getLoanSettlementInfo(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/settlementInfo';
	}

	getAllTasks(type?: number) {
		let address = this.apiAddress.ALL_TASKS;

		if (type != undefined) address += '?type=' + type;
		return address;
	}

	getAllCompanies(sharable?: boolean) {
		let address = this.apiAddress.ALL_COMPANIES;

		if (sharable != undefined) {
			const sharableStr = sharable == true ? 'true' : 'false';
			address += '?sharable =' + sharableStr;
		}

		return address;
	}

	getAllCompaniesDetails(lastId?: number, limit?: number) {
		let address = this.apiAddress.ALL_COMPANIES + '/details';

		if (lastId != undefined || limit != undefined) address += '?';

		if (lastId != undefined) address += 'lastId=' + lastId + '&';
		if (limit != undefined) address += 'limit=' + limit;

		return address;
	}

	getCompanyGroupSummaries(groupId: number) {
		return this.apiAddress.COMPANY_GROUP + '/' + groupId + '/stats/summaries';
	}

	getAllCompanySummaires() {
		return this.apiAddress.ALL_STATS + '/summaries';
	}

	getAllAccounts() {
		return this.apiAddress.ALL_ACCOUNTS;
	}

	getAllLoanSchemes() {
		return this.apiAddress.ALL_LOANSCHEMES;
	}

	getAllLeads() {
		return this.apiAddress.ALL_LEADS;
	}

	getAllLeadsProfiles() {
		return this.apiAddress.ALL_LEADS + '/profiles';
	}

	getCompanyLeadsProfiles(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/leads/profiles';
	}

	getLeadDetails(id: number) {
		return this.apiAddress.LEAD + '/' + id;
	}

	getAllCustomers(profileId?: number) {
		let address = this.apiAddress.ALL_CUSTOMERS;
		if (profileId != undefined) address += '?profileId=' + profileId;

		return address;
	}

	getCustomerByProfileIds(profileIds?: number[], sharedProfileIds?: number[]) {
		let address = this.apiAddress.ALL_CUSTOMERS + '/profiles';
		const params = new URLSearchParams();

		if (profileIds != undefined) profileIds.forEach(id => params.append('id', id.toString()));
		if (sharedProfileIds != undefined) sharedProfileIds.forEach(id => params.append('sharedProfileId', id.toString()));

		if (params.toString().length > 0) address = [address, params.toString()].join('?');
		return address;
	}

	getCompanyAllCustomers(companyId: number, verified?: boolean) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/customers';
		if (verified != undefined) address += '?verified=' + verified;

		return address;
	}

	getCompanyAllLoans(companyId: number, scheme?: string, status?: number[]) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/loans';
		if (scheme != undefined) address += '?scheme=' + scheme;

		if (status != undefined) {
			for (let i = 0; i < status.length; i++) {
				address += '&status=' + status[i];
			}
		}

		return address;
	}

	getCustomerAllLoans(customerId: number, status?: number[]) {
		let address = this.apiAddress.CUSTOMER + '/' + customerId + '/loans';

		if (status != undefined) {
			for (let i = 0; i < status.length; i++) {
				address += '?status=' + status[i];
			}
		}

		return address;
	}

	getAllAdvances(paid?: boolean, minAmount?: number, maxAmount?: number, companyId?: number) {
		let address = this.apiAddress.ALL_ADVANCES;

		if (paid != undefined || minAmount != undefined || maxAmount != undefined || companyId != undefined) address += '?';
		if (paid != undefined) address += 'paid=' + paid + '&';
		if (minAmount != undefined) address += 'minAmount=' + minAmount + '&';
		if (maxAmount != undefined) address += 'maxAmount=' + maxAmount + '&';
		if (companyId != undefined) address += 'companyId=' + companyId + '&';

		return address;
	}

	getAllAdvancesOfCompany(companyID: number, paid?: boolean, minAmount?: number, maxAmount?: number, endDate?: string) {
		let address = this.apiAddress.COMPANY + '/' + companyID + '/advances';

		if (paid != undefined || minAmount != undefined || maxAmount != undefined || companyID != undefined || endDate != undefined) address += '?';

		if (endDate != undefined) address += '?endDate=' + endDate;

		if (paid != undefined) address += 'paid=' + paid + '&';

		if (minAmount != undefined) address += 'minAmount=' + minAmount + '&';

		if (maxAmount != undefined) address += 'maxAmount=' + maxAmount + '&';

		if (companyID != undefined) address += 'companyId=' + companyID + '&';
		return address;
	}

	getAllUsers(companyId?: number, roleId?: number[], isActive?: boolean) {
		let address = this.apiAddress.ALL_USERS;

		const p: Record<string, any> = {};
		if (companyId !== undefined) p.companyId = companyId;
		if (isActive !== undefined) p.active = isActive;
		const params = new URLSearchParams(p);

		roleId?.forEach(r => params.append('roleId', r.toString()));

		if (params.toString().length > 0) address = [address, params.toString()].join('?');
		return address;
	}

	getAllCompanyGroups() {
		return this.apiAddress.ALL_COMPANY_GROUPS;
	}

	getCompanyGroupCompanies(groupId: number) {
		return this.apiAddress.COMPANY_GROUP + '/' + groupId + '/companies';
	}

	getAllPendingCommissions(profileId?: number, assignable?: boolean, lastDate?: string) {
		let address = this.apiAddress.ALL_COMMISSION + '/pending';
		if (profileId != undefined || assignable != undefined || lastDate != undefined) address += '?';

		if (profileId != undefined) address += 'profileId=' + profileId + '&';

		if (assignable != undefined) address += 'assignable=' + assignable + '&';

		if (lastDate != undefined) address += 'lastDate=' + lastDate + '&';

		return address;
	}

	getAllCommissions() {
		return this.apiAddress.ALL_COMMISSION;
	}

	getEditCommission(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/commission';
	}

	getDeleteLoanCommission(loanId: number, commissionId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/commission/' + commissionId;
	}

	getLoanCommissions(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/commission';
	}

	getAllExpenses() {
		return this.apiAddress.ALL_EXPENSES;
	}

	getAllTransactionTypes() {
		return this.apiAddress.TRANSACTION_TYPES;
	}

	getAllTransactionSubTypes() {
		return this.apiAddress.ALL_SUBTYPES;
	}

	getTransactionSubTypes(type: number) {
		return this.apiAddress.TRANSACTION_TYPE + '/' + type + '/subtypes';
	}

	getAllAssetsOfCompany(companyId: number, endDate?: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/assets';

		if (endDate != undefined) address += '?endDate=' + endDate;

		return address;
	}

	getProfilesReserveReceipts(startDate?: string, endDate?: string, companyId?: number) {
		// var address = this.apiAddress.ALL_PROFILES + "/reserve";
		let address = this.apiAddress.ALL_RESERVES + '/receipts';
		if (startDate != undefined || endDate != undefined || companyId != undefined) address += '?';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (companyId != undefined) address += 'companyId=' + companyId;

		return address;
	}

	getCompanyActiveReserve(companyId: number, endDate?: string, minAmount?: number) {
		// var address = this.apiAddress.ALL_PROFILES + "/reserve";
		let address = this.apiAddress.COMPANY + '/' + companyId + '/report/activeReserves';
		if (endDate != undefined || minAmount != undefined) address += '?';

		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (minAmount != undefined) address += 'minAmount=' + minAmount;

		return address;
	}

	getProfileAllLoansTransactions(
		profileId: number,
		startDate?: string,
		endDate?: string,
		scheme?: string,
		type?: number[],
		accountId?: number,
		handledBy?: string,
		minAmount?: number,
		maxAmount?: number
	) {
		let address = this.apiAddress.PROFILE + '/' + profileId + '/transactions/loans';

		if (scheme != undefined || startDate != undefined || endDate != undefined || type != undefined || handledBy != undefined) address += '?';

		if (type != undefined) {
			for (let i = 0; i < type.length; i++) {
				address += 'type=' + type[i] + '&';
			}
		}

		if (scheme != undefined) address += 'scheme=' + scheme + '&';
		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate + '&';
		if (accountId != undefined) address += 'accountId=' + accountId + '&';

		if (handledBy != undefined && handledBy != '') address += 'handledBy=' + handledBy + '&';
		return address;
	}

	getAllTransactionsForTransactionGroup(companyId: number, groupId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/transactions/group/' + groupId;
	}

	getCompanyAllLoansTransactions(
		companyId: number,
		startDate?: string,
		endDate?: string,
		scheme?: string,
		type?: number[],
		accountId?: number,
		handledBy?: string,
		customerId?: number,
		minAmount?: number,
		maxAmount?: number,
		inSet?: boolean
	) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/transactions/loans';

		if (
			scheme != undefined ||
			startDate != undefined ||
			endDate != undefined ||
			type != undefined ||
			handledBy != undefined ||
			customerId != undefined ||
			minAmount != undefined ||
			maxAmount != undefined ||
			inSet != undefined
		)
			address += '?';

		if (type != undefined) {
			for (let i = 0; i < type.length; i++) {
				address += 'type=' + type[i] + '&';
			}
		}

		if (scheme != undefined) address += 'scheme=' + scheme + '&';
		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate + '&';
		if (accountId != undefined) address += 'accountId=' + accountId + '&';

		if (handledBy != undefined && handledBy != '') address += 'handledBy=' + handledBy + '&';
		if (customerId != undefined) address += 'customerId=' + customerId + '&';
		if (minAmount != undefined) address += 'minAmount=' + minAmount + '&';
		if (maxAmount != undefined) address += 'maxAmount=' + maxAmount + '&';
		if (inSet != undefined) address += 'inSet=' + inSet;
		return address;
	}

	getCompanyCashBookBalance(companyId: number, endDate: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/cashBookBalance';
		if (endDate != undefined) address += '?endDate=' + endDate;
		return address;
	}

	getSchemeSummaries(companyId: number, endDate: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/stats/schemeSummaries';
		if (endDate != undefined) address += '?endDate=' + endDate;
		return address;
	}

	getCompaniesOutstandingSummaries(endDate?: string) {
		let address = this.apiAddress.ALL_STATS + '/outstandingSummaries';
		if (endDate != undefined) address += '?endDate=' + endDate;
		return address;
	}
	getCompaniesOutstandingSummariesByGroup(companyGroup: number, endDate?: string) {
		let address = this.apiAddress.COMPANY_GROUP + '/' + companyGroup + '/stats/outstandingSummaries';
		if (endDate != undefined) address += '?endDate=' + endDate;
		return address;
	}

	getCompaniesCustomerCount(endDate?: string) {
		let address = this.apiAddress.ALL_STATS + '/customerCountByStatus';
		if (endDate != undefined) address += '?endDate=' + endDate;
		return address;
	}

	getCompaniesCustomerCountByGroup(companyGroup: number, endDate?: string) {
		let address = this.apiAddress.COMPANY_GROUP + '/' + companyGroup + '/stats/customerCountByStatus';
		if (endDate != undefined) address += '?endDate=' + endDate;
		return address;
	}

	getCompaniesCashBalance(endDate?: string, limit?: number) {
		let address = this.apiAddress.ALL_COMPANIES + '/balance';

		if (endDate != undefined || limit != undefined) address += '?';
		if (endDate != undefined) address += 'endDate=' + endDate;
		if (limit != undefined) address += '&limit=' + limit;
		return address;
	}

	getCompanyGroupCompaniesCashBalance(groupId: number, endDate?: string, limit?: number) {
		let address = this.apiAddress.COMPANY_GROUP + '/' + groupId + '/companies/balance';

		if (endDate != undefined || limit != undefined) address += '?';
		if (endDate != undefined) address += 'endDate=' + endDate;
		if (limit != undefined) address += '&limit=' + limit;
		return address;
	}

	getAccount(accountId: number, endDate?: string) {
		let address = this.apiAddress.ACCOUNT + '/' + accountId;
		if (endDate != undefined) address += '?endDate=' + endDate;

		return address;
	}

	getCompanyLeads(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/leads';
	}

	getCustomemerOfUser(username: string) {
		return this.apiAddress.USER_CUSTOMERS + username;
	}

	getTaskOfUser(username: string) {
		return this.apiAddress.USER_TASK + username;
	}

	getLoanOfCustomer(customerID: number, status?: number[], schemes?: string[], withLegacyEPF?: boolean, sharedProfileId?: number, includedSharedProfile?: boolean) {
		const address = this.apiAddress.CUSTOMER + '/' + customerID + '/loans';

		const p: Record<string, any> = {};

		if (withLegacyEPF != undefined) p.withLegacyEPF = withLegacyEPF;
		if (sharedProfileId != undefined) p.sharedProfileId = sharedProfileId;
		if (includedSharedProfile != undefined) p.inclShared = includedSharedProfile;
		const params = new URLSearchParams(p);
		if (status?.length > 0) status.forEach(s => params.append('status', s.toString()));
		if (schemes?.length > 0) schemes.forEach(s => params.append('scheme', s.toString()));

		return [address, params.toString()].join('?');
	}

	getLoanOfCustomerProfile(profileID: number, status?: number[], schemes?: string[], withLegacyEPF?: boolean, includeShared?: boolean) {
		const address = this.apiAddress.PROFILE + '/' + profileID + '/loans';

		const p: Record<string, any> = {};

		if (withLegacyEPF != undefined) p.withLegacyEPF = withLegacyEPF;
		if (includeShared != undefined) p.inclShared = includeShared;
		const params = new URLSearchParams(p);
		if (status?.length > 0) status.forEach(s => params.append('status', s.toString()));
		if (schemes?.length > 0) schemes.forEach(s => params.append('scheme', s.toString()));

		return params.toString().length > 0 ? `${address}?${params.toString()}` : address;
	}

	getCompanyLoans(
		companyId: number,
		status?: number[],
		schemes?: string[],
		withLegacyEPF?: boolean,
		sharedProfileId?: number,
		includedSharedProfile?: boolean,
		customerId?: number
	) {
		const address = this.apiAddress.COMPANY + '/' + companyId + '/loans';

		const p: Record<string, any> = {};

		if (withLegacyEPF != undefined) p.withLegacyEPF = withLegacyEPF;
		if (sharedProfileId != undefined) p.sharedProfileId = sharedProfileId;
		if (includedSharedProfile != undefined) p.inclShared = includedSharedProfile;
		if (customerId != undefined) p.customerId = customerId;
		const params = new URLSearchParams(p);
		if (status?.length > 0) status.forEach(s => params.append('status', s.toString()));
		if (schemes?.length > 0) schemes.forEach(s => params.append('scheme', s.toString()));

		return params.toString().length > 0 ? `${address}?${params.toString()}` : address;
	}

	getUpdateLoanDetail(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId;
	}

	getCustomerLoanAllFiles(customerId: number, loanId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/loan/' + loanId + '/files';
	}

	getTransactionOfUser(username: string) {
		return this.apiAddress.USER_TRANSACTIONS + username;
	}

	getTransactionOfAccount(accountId: number) {
		return this.apiAddress.ACCOUNT_TRANSACTIONS + accountId;
	}

	getStatusOfLoan(loanId: number) {
		return this.apiAddress.LOAN_STATS + loanId;
	}

	getCustomerDetails(customerId: number) {
		return this.apiAddress.CUSTOMER_DETAILS + customerId;
	}

	getATransactionByID(transactionId: number) {
		return this.apiAddress.TRANSACTION + '/' + transactionId;
	}

	getTaskDetails(taskId: number) {
		return this.apiAddress.TASK + '/' + taskId;
	}

	getTaskOfCustomer(customerID: number) {
		return this.apiAddress.CUSTOMER_DETAILS + customerID + '/tasks';
	}

	getTaskOfProfile(profileID: number) {
		return this.apiAddress.PROFILE + '/' + profileID + '/tasks';
	}

	getAddApprovedAsset() {
		return this.apiAddress.ADD_APPROVEDASSET;
	}

	getAddLoan() {
		return this.apiAddress.ADD_LOAN;
	}
	getAddLoans() {
		return this.apiAddress.ALL_LOANS;
	}

	getAddAsset() {
		return this.apiAddress.ADD_ASSET;
	}

	getApproveAsset(assetId: number) {
		return this.apiAddress.ADD_ASSET + '/' + assetId + '/approve';
	}

	getAddCustomer() {
		return this.apiAddress.ADD_CUSTOMER;
	}

	getAddCPSettledCustomer() {
		return this.apiAddress.ADD_CUSTOMER + '/CP';
	}

	getAddProfile() {
		return this.apiAddress.PROFILE;
	}

	getAddAcccount() {
		return this.apiAddress.ADD_ACCOUNT;
	}

	getAddReserve(profileID: number) {
		return this.apiAddress.PROFILE + '/' + profileID + '/reserve/income';
	}

	//withdraw simpan to duit lebih
	getWithdrawReserve(profileID: number) {
		return this.apiAddress.PROFILE + '/' + profileID + '/reserve/withdrawal';
	}

	getTransferReserve(profileID: number) {
		return this.apiAddress.PROFILE + '/' + profileID + '/reserve/transfer';
	}

	getExpenseReserve(profileID: number) {
		return this.apiAddress.PROFILE + '/' + profileID + '/reserve/expense';
	}

	getAddCompany() {
		return this.apiAddress.ADD_COMPANY;
	}

	getAddTask() {
		return this.apiAddress.TASK;
	}

	getAddIncome() {
		return this.apiAddress.INCOME;
	}

	getAddIncomeOfExtras() {
		return this.apiAddress.INCOME_EXTRAS;
	}

	getAddLoanScheme() {
		return this.apiAddress.LOANSCHEME;
	}
	getUpdateCompany(companyID: number) {
		return this.apiAddress.UPDATE_COMPANY + companyID;
	}

	getUpdateCustomer(customerID: number) {
		return this.apiAddress.UPDATE_CUSTOMER + customerID;
	}

	getUpdateProfile(profileID: number) {
		return this.apiAddress.PROFILE + '/' + profileID;
	}

	getGlobalSearch(name: string, ic: string) {
		let address = this.apiAddress.GLOBAL_SEARCH;
		if (name && name != '') {
			address += 'name=' + this.sharedService.processStringStripInvalidSymbol(name);

			if (ic && ic != '') {
				address += '&icNumber=' + this.sharedService.processStringStripInvalidSymbol(ic) + '';
			}
		} else {
			if (ic && ic != '') {
				address += 'icNumber=' + this.sharedService.processStringStripInvalidSymbol(ic);
			}
		}

		return address;
	}

	getCompanyCustomerSearch(companyId: number, name?: string, ic?: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/customers/search?';
		if (name && name != '') {
			address += 'name=' + this.sharedService.processStringStripInvalidSymbol(name);

			if (ic && ic != '') {
				address += '&icNumber=' + this.sharedService.processStringStripInvalidSymbol(ic) + '';
			}
		} else {
			if (ic && ic != '') {
				address += 'icNumber=' + this.sharedService.processStringStripInvalidSymbol(ic);
			}
		}

		return address;
	}

	getCompanyProfileSearch(companyId: number, name: string, ic: string, custCode: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/profiles/search?';

		if (name != undefined && name != '') address += 'name=' + this.sharedService.processStringStripInvalidSymbol(name) + '&';

		if (ic != undefined && ic != '') address += 'icNumber=' + this.sharedService.processStringStripInvalidSymbol(ic) + '&';

		if (custCode != undefined && custCode != '') address += 'customerCode=' + this.sharedService.processStringStripInvalidSymbol(custCode) + '&';
		return address;
	}

	getSearchCustomerByReceipt(companyId: number, receiptNumber: string) {
		return this.apiAddress.COMPANY + '/' + companyId + '/loan/receipt/' + receiptNumber;
	}

	getCompanyAccountsOfCompany(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/accounts';
	}

	getAccountsOfCompany(companyId: number, endDateStr?: string) {
		let address = this.apiAddress.ALL_ACCOUNTS + '?companyId=' + companyId;

		if (endDateStr != undefined) address += '&endDate=' + endDateStr;

		return address;
	}

	getAddPayment(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/payment';
	}

	getAddBatchPayment() {
		return this.apiAddress.ALL_LOANS + '/payments';
	}

	getAddBatchSimpanPayment() {
		return this.apiAddress.ALL_LOANS + '/payments/reserve';
	}

	getAddPaymentWithReserve(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/payment/reserve';
	}

	getAddTopUp(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/topup';
	}

	getAddReserveWithdrawal(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/withdrawal';
	}

	getTasksForAday(date: string, type?: number) {
		let address = this.apiAddress.ALL_TASKS + '/' + date;
		if (type != undefined) address += '?type=' + type;

		return address;
	}

	getUserCashAccount(username: string) {
		return this.apiAddress.USER + '/' + username + '/account';
	}

	getUpdateTask(taskID: number) {
		return this.apiAddress.TASK + '/' + taskID;
	}

	getDeleteLoanScheme(schemeID: number) {
		return this.apiAddress.LOANSCHEME + '/' + schemeID;
	}
	getDeleteTransactionSubType(subTypeId: number) {
		return this.apiAddress.SUBTYPE + '/' + subTypeId;
	}

	getDeleteTransaction(transactionId: number) {
		return this.apiAddress.TRANSACTION + '/' + transactionId;
	}

	getDeleteLoanTransaction(transactionId: number) {
		return this.apiAddress.ALL_LOANS + '/receipts/transaction/' + transactionId;
	}

	getDeleteGroupTransaction(companyId: number, groupId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/transactions/group/' + groupId;
	}

	getDeleteReserveTransaction(transactionId: number) {
		return this.apiAddress.ALL_RESERVES + '/receipts/transaction/' + transactionId;
	}

	getDeleteBadDebtTransaction(transactionId: number) {
		return this.apiAddress.ALL_BADDEBTS + '/receipts/transaction/' + transactionId;
	}

	getDeleteAdvanceTransaction(transactionId: number) {
		return this.apiAddress.ALL_ADVANCES + '/receipts/transaction/' + transactionId;
	}

	getDeleteAssetTransaction(transactionId: number) {
		return this.apiAddress.ALL_ASSETS + '/receipts/transaction/' + transactionId;
	}

	getDeleteCheckIn(companyId: number, checkInId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/checkIn/' + checkInId;
	}

	getDeleteCheckInSinceDate(companyId: number, checkInDate: Date) {
		const dateStr = new Date(checkInDate);
		const encodedStr = this.sharedService.createLocalISODateEncodedString(dateStr);
		// var encodedStr = dateStr.replace(':', "%3A").replace('+', "%2B").replace('Z', '');
		return this.apiAddress.COMPANY + '/' + companyId + '/checkIns?sinceDate=' + encodedStr;
	}

	getTaskCollection(companyId: number, dateStr: string) {
		return this.apiAddress.COMPANY + '/' + companyId + '/collection/' + dateStr;
	}

	getLoanReceipts(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/receipts';
	}

	getDuitLebihReceipts(companyID: number) {
		return this.apiAddress.COMPANY + '/' + companyID + '/extras';
	}

	getAllTransactions(
		companyID: number,
		type: number[],
		startDate?: string,
		endDate?: string,
		maxAmount?: number,
		minAmount?: number,
		accountId?: number,
		handledBy?: string,
		inSet?: boolean
	) {
		let address = this.apiAddress.COMPANY + '/' + companyID + '/transactions' + '?';
		for (let i = 0; i < type.length; i++) {
			address += 'type=' + type[i] + '&';
		}

		if (startDate != undefined) {
			address += 'startDate=' + startDate + '&';
		}

		if (endDate != undefined) {
			address += 'endDate=' + endDate + '&';
		}

		if (minAmount != undefined) {
			address += 'minAmount=' + minAmount + '&';
		}

		if (maxAmount != undefined) {
			address += 'maxAmount=' + maxAmount + '&';
		}
		if (accountId != undefined) {
			address += 'accountId=' + accountId + '&';
		}

		if (handledBy != undefined) address += 'handledBy=' + handledBy;

		if (inSet != undefined) address += 'inSet=' + inSet;

		return address;
	}

	getAllCompanyGroupTransactions(groupId: number, type: number[], startDate?: string, endDate?: string, maxAmount?: number, minAmount?: number) {
		let address = this.apiAddress.COMPANY_GROUP + '/' + groupId + '/transactions' + '?';
		for (let i = 0; i < type.length; i++) {
			address += 'type=' + type[i] + '&';
		}

		if (startDate != undefined) {
			address += 'startDate=' + startDate + '&';
		}

		if (endDate != undefined) {
			address += 'endDate=' + endDate + '&';
		}

		if (minAmount != undefined) {
			address += 'minAmount=' + minAmount + '&';
		}

		if (maxAmount != undefined) {
			address += 'maxAmount=' + maxAmount + '&';
		}
		return address;
	}

	getTransactionBalance(type: number, endDate?: string) {
		let address = this.apiAddress.TRANSACTION_BALANCE + '/' + type;
		if (endDate != undefined) address += '?endDate=' + endDate;
		return address;
	}

	getCompanyTransactionBalances(companyId: number, endDate?: string, type?: number) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/transactionBalances';

		if (endDate != undefined || type != undefined) address += '?';

		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (type != undefined) address += 'type=' + type;
		return address;
	}

	getCompanyTransactionBalancesDetails(companyId: number, startDate?: string, endDate?: string, types?: number[]) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/transactionBalances/details';

		if (endDate != undefined || types != undefined || startDate != undefined) address += '?';
		if (startDate != undefined) address += 'startDate=' + startDate + '&';

		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (types != undefined) {
			for (let i = 0; i < types.length; i++) {
				address += 'type=' + types[i] + '&';
			}
		}

		return address;
	}

	getTransactionBalances(companyGroup?: number, companyId?: number, endDate?: string, types?: number[]) {
		let address = this.apiAddress.ALL_TRANSACTION_BALANCES;
		const params = new URLSearchParams();

		if (companyGroup != undefined) params.set('groupId', companyGroup.toString());
		if (companyId != undefined) params.set('companyId', companyId.toString());
		if (endDate != undefined) params.set('endDate', endDate);
		types?.forEach(id => params.append('type', id.toString()));

		if (params.toString().length > 0) address = [address, params.toString()].join('?');
		return address;
	}

	getTransactionTotal(startDate: string, endDate: string, types: number[], companyGroup?: number) {
		let address = this.apiAddress.ALL_TRANSCACTION_TOTAL;

		if (companyGroup != undefined || startDate != undefined || endDate != undefined || types != undefined) address += '?';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';

		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (companyGroup != undefined) address += 'groupId=' + companyGroup + '&';

		for (let i = 0; i < types.length; i++) {
			address += 'type=' + types[i] + '&';
		}
		return address;
	}

	getTransactionDetails(startDate: string, endDate: string, types: number[], companyGroup?: number) {
		let address = this.apiAddress.ALL_TRANSCACTION_TOTAL + '/details';

		if (startDate != undefined || endDate != undefined || types != undefined || companyGroup != undefined) address += '?';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';

		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (companyGroup != undefined) address += 'groupId=' + companyGroup + '&';

		for (let i = 0; i < types.length; i++) {
			address += 'type=' + types[i] + '&';
		}
		return address;
	}

	getCompanyTransactionDetails(companyId: number, startDate: string, endDate: string, types: number[], companyGroup?: number) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/transactionTotals/details';

		if (startDate != undefined || endDate != undefined || types != undefined || companyGroup != undefined) address += '?';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';

		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (companyGroup != undefined) address += 'groupId=' + companyGroup + '&';

		for (let i = 0; i < types.length; i++) {
			address += 'type=' + types[i] + '&';
		}
		return address;
	}

	getTransactionBalanceDetails(companyIds: number[], types?: number[], startDate?: string, endDate?: string, companyGroupID?: number) {
		let address = this.apiAddress.ALL_TRANASACTION_BALANCES_DETAILS + '?';

		if (companyIds != undefined) {
			for (let i = 0; i < companyIds.length; i++) {
				address += 'companyId=' + companyIds[i] + '&';
			}
		}

		if (companyGroupID != undefined) address += 'groupId=' + companyGroupID + '&';

		if (types != undefined) {
			for (let i = 0; i < types.length; i++) {
				address += 'type=' + types[i] + '&';
			}
		}

		if (startDate != undefined) address += 'startDate=' + startDate + '&';

		if (endDate != undefined) address += 'endDate=' + endDate + '&';
		return address;
	}

	getCustomerReserveReceipts(customerId: number, startDate?: string, endDate?: string) {
		let address = this.apiAddress.CUSTOMER + '/' + customerId + '/reserves';

		if (startDate != undefined || endDate != undefined) address += '?';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate;

		return address;
	}

	getProfileReserveReceipts(profileID: number, startDate?: string, endDate?: string) {
		let address = this.apiAddress.PROFILE + '/' + profileID + '/reserves';

		if (startDate != undefined || endDate != undefined) address += '?';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate;

		return address;
	}

	getCompanyReserveReceipts(companyId: number, startDate?: string, endDate?: string) {
		let address = this.apiAddress.ALL_RESERVES + '/receipts?companyId=' + companyId;

		if (startDate != undefined || endDate != undefined) address += '&';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate;

		return address;
	}

	getCompanyBDReserveReceipts(companyId: number, startDate?: string, endDate?: string) {
		let address = this.apiAddress.ALL_BADDEBTS + '/receipts?companyId=' + companyId;

		if (startDate != undefined || endDate != undefined) address += '&';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate;

		return address;
	}

	getProfileBDReceipts(profileID: number, startDate?: string, endDate?: string) {
		let address = this.apiAddress.PROFILE + '/' + profileID + '/badDebts';

		if (startDate != undefined || endDate != undefined) address += '?';

		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate;

		return address;
	}

	getLoanArrear(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/arrears';
	}

	getLoanArrearPayment(arrearId: number) {
		return this.apiAddress.ARREAR + '/' + arrearId + '/payment';
	}

	getAddContact() {
		return this.apiAddress.CONTACT;
	}

	getDeleteContact(id: number) {
		return this.apiAddress.CONTACT + '/' + id;
	}

	getUpdateContact(id: number) {
		return this.apiAddress.CONTACT + '/' + id;
	}

	getDeleteCompany(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId;
	}

	getCustomerContact(customerID: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/contacts';
	}

	getCustomerContactWithContactID(customerID: number, contactId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/contact/' + contactId;
	}

	getCustomerContactAllFiles(customerId: number, contactId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/files';
	}

	getContactDetails(contactId: number) {
		return this.apiAddress.CONTACT + '/' + contactId;
	}

	getUpdateDoneTask(taskID: number) {
		return this.apiAddress.TASK + '/' + taskID + '/done';
	}

	getAccountTransactions(accountId: number, startDate?: string, endDate?: string, type?: number[], handledBy?: string) {
		let address = this.apiAddress.ACCOUNT + '/' + accountId + '/transactions';

		if (startDate != undefined || endDate != undefined || type != undefined || handledBy != undefined) address += '?';

		if (type != undefined) {
			for (let i = 0; i < type.length; i++) {
				address += 'type=' + type[i] + '&';
			}
		}

		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (handledBy != undefined || handledBy != '') address += 'handledBy=' + handledBy + '&';

		return address;
	}

	getUserYearMonthLoanStat(username: string, year: number, month: number) {
		if (month < 10) return this.apiAddress.USER + '/' + username + '/stats/loans/' + year + '-0' + month;
		else return this.apiAddress.USER + '/' + username + '/stats/loans/' + year + '-' + month;
	}

	getYearMonthLoanStat(year: number, month: number) {
		if (month < 10) return this.apiAddress.STAT_LOANS + '/' + year + '-0' + month;
		else return this.apiAddress.STAT_LOANS + '/' + year + '-' + month;
	}

	getAddExpenses() {
		return this.apiAddress.EXPENSES;
	}

	getAddCompanyExpense() {
		return this.apiAddress.EXPENSE;
	}

	getAddExtraExpenses() {
		return this.apiAddress.EXPENSES_EXTRAS;
	}

	getAddCompanyExpenseByExtras() {
		return this.apiAddress.EXPENSE_EXTRAS;
	}

	getPostFundAllocation() {
		return this.apiAddress.REALLOCATION;
	}

	getPostFundTransfer(accountId: number) {
		return this.apiAddress.ACCOUNT + '/' + accountId + '/transfer';
	}

	getAddCapital() {
		return this.apiAddress.CAPITAL;
	}

	getReturnCapital() {
		return this.apiAddress.CAPITAL + '/return';
	}

	getAddToting() {
		return this.apiAddress.TOTING;
	}

	getReturnToting() {
		return this.apiAddress.TOTING + '/return';
	}

	getAddSaving() {
		return this.apiAddress.SAVING;
	}

	getExtraBalance(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/extras/balance';
	}

	getAdvanceById(advanceId: number) {
		return this.apiAddress.ADVANCE + '/' + advanceId;
	}

	getAddTransactionSubType() {
		return this.apiAddress.SUBTYPE;
	}

	getUploadFile(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/file';
	}

	getDeleteFile(customerId: number, filename: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/file/' + filename;
	}

	getUploadApplicationForm(customerId: number, supportingDocType: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/file/' + supportingDocType;
	}

	getUploadContactGuarantorForm(customerId: number, contactId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/file/guarantorForm';
	}

	getUploadContactPhoto(customerId: number, contactId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/file/photo';
	}

	getUploadContactTypedFile(customerId: number, contactId: number, type: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/file/' + type;
	}

	getUploadLoanTypedFile(customerId: number, loanId: number, type: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/loan/' + loanId + '/file/' + type;
	}

	getUploadReserveGroupFile(reserveGroupId: number) {
		return this.apiAddress.RESERVE_GROUP + '/' + reserveGroupId + '/file';
	}

	getUploadCustomerAcctFile(customerId: number, acctId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/account/' + acctId + '/file';
	}

	getUploadCustomerAcctTypedFile(customerId: number, acctId: number, type: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/account/' + acctId + '/file/' + type;
	}

	getReserveGroupAllFiles(reserveGroupId: number) {
		return this.apiAddress.RESERVE_GROUP + '/' + reserveGroupId + '/files';
	}

	getReserveGroupFileFull(reserveGroupId: number, filename: string) {
		return this.apiAddress.RESERVE_GROUP + '/' + reserveGroupId + '/file/' + filename;
	}

	getBlacklistFileFull(blacklistId: number, filename: string) {
		return this.apiAddress.BLACKLIST + '/' + blacklistId + '/file/' + filename;
	}

	getCustomerAcctAllFiles(custID: number, custAcctId: number) {
		return this.apiAddress.CUSTOMER + '/' + custID + '/account/' + custAcctId + '/files';
	}

	getBlacklistAllFiles(blacklistId: number) {
		return this.apiAddress.BLACKLIST + '/' + blacklistId + '/files';
	}
	getUploadLoanNormalFile(customerId: number, loanId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/loan/' + loanId + '/file';
	}

	getAllFiles(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/files';
	}

	getAllContactPhotos(customerId: number, contactId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/files';
	}
	getCustomerContactGuarantorForms(customerId: number, contactId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/files/guarantorForm';
	}

	getFullFile(customerId: number, fileName: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/file/' + fileName;
	}

	getSendToCP(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/CP';
	}

	getSendProfileLoansToCP(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/CP';
	}

	getSendProfileLoansToLBD(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/lbd';
	}

	getRemoveFromCP(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/unCP';
	}

	getPayCustomerBadDebt(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/badDebt/payment';
	}

	getPayProfileBadDebt(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/badDebt/payment';
	}

	getPayCompanyUnpaidBadDebts(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/badDebts/payment';
	}

	getCompanyUnpaidBadDebts(companyId: number, endDate?: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/badDebts';

		if (endDate != undefined) address += '?endDate=' + endDate;

		return address;
	}

	getWithdrawBadDebtsToSimpan(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/badDebt/withdrawal';
	}

	getSearchTaskByCriteria(username: string, done: boolean, customerId: number, customerName: string, icNumber: string) {
		let address = this.apiAddress.ALL_TASKS + '?';
		if (username && username != '') {
			address += 'username=' + username + '&';
		}
		if (done != undefined) {
			address += 'done=' + done + '&';
		}

		if (customerId != undefined) {
			address += 'customerId=' + customerId + '&';
		}

		if (customerName != undefined && customerName != '') {
			address += 'customerName=' + customerName + '&';
		}

		if (icNumber != undefined && icNumber != '') {
			address += 'icNumber=' + icNumber + '&';
		}
		return address;
	}

	getSignUp() {
		return this.apiAddress.SIGN_UP;
	}

	getSignIn() {
		return this.apiAddress.SIGN_IN;
	}

	getCompanyUnpaidCommission(companyId: number, endDate?: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/commissions?paid=false';

		if (endDate != undefined) address += '&endDate=' + endDate;

		return address;
	}

	getAllUnpaidCommission() {
		return this.apiAddress.ALL_COMMISSION + '?paid=false';
	}

	getCompanyDetails(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId;
	}

	getCompanyPaidCommission(companyId: number, startDate?: string, endDate?: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/commissions?paid=true';

		if (startDate != undefined) address += '&startDate=' + startDate;

		if (endDate != undefined) address += '&endDate=' + endDate;

		return address;

		// return this.apiAddress.COMPANY + "/" + companyId;
	}

	getAddCommissionPayout() {
		return this.apiAddress.ALL_COMMISSION + '/payment';
	}

	getAssignCommission() {
		return this.apiAddress.COMMISSION;
	}

	getAssignLoanCommission(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/commission';
	}

	getPaidLoanDetail(loanId: number, Ids?: number[]) {
		if (Ids == undefined) return this.apiAddress.ALL_LOANS + '/paid?id=' + loanId;
		else {
			const address = this.apiAddress.ALL_LOANS + '/paid';
			let idStr = '';
			for (let i = 0; i < Ids.length; i++) {
				if (idStr == '') idStr += '?id=' + Ids[i];
				else idStr += '&id=' + Ids[i];
			}

			return address + idStr;
		}
	}

	getAllPaidLoanDetail() {
		return this.apiAddress.ALL_LOANS + '/paid';
	}

	getCompanyPaidLoanDetail(companyId: number, endDateStr?: string) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/loans/paid';

		if (endDateStr != undefined) {
			address += '?endDate=' + endDateStr;
		}

		return address;
	}

	getUpdateUserSalary(username: string) {
		return this.apiAddress.USER + '/' + username + '/salary';
	}

	getWithdrawStampOffice() {
		return this.apiAddress.STAMP_OFFICE;
	}

	getWithdrawStampHQContra() {
		return this.apiAddress.STAMP_HQ + '/contra';
	}

	getWithdrawServiceCharge() {
		return this.apiAddress.SERVICE_CHARGE;
	}

	getWithdrawHandling() {
		return this.apiAddress.HANDLING_CHARGE;
	}

	getAddImportFile(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/import';
	}

	getAddImportBalance(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/import/balances';
	}

	getEPFCustomerExcelReport() {
		return this.apiAddress.EPF_EXCEL_REPORT;
	}

	getExtrasReport(companyId: number, startDate: string, endDate: string) {
		return this.apiAddress.COMPANY + '/' + companyId + '/report/extras?startDate=' + startDate + '&endDate=' + endDate;
	}

	getMidMonth(companyId: number, startDate: string, endDate: string, omitSchemeA: boolean, omitInfo: boolean) {
		return this.apiAddress.COMPANY + '/' + companyId + '/report/midMonth?startDate=' + startDate + '&endDate=' + endDate + '&omitSchemeA=' + omitSchemeA + '&omitInfo=' + omitInfo;
	}

	getMidMonthV1(companyId: number, startDate: string, endDate: string, omitSchemeA: boolean, omitInfo: boolean) {
		return (
			this.apiAddress.COMPANY + '/' + companyId + '/report/midMonth/v1?startDate=' + startDate + '&endDate=' + endDate + '&omitSchemeA=' + omitSchemeA + '&omitInfo=' + omitInfo
		);
	}

	// #region 'Form J'
	getAllFormJs(
		scheme?: string,
		hasLoanGroup?: boolean,
		businessId?: number,
		branchId?: number,
		licenseId?: number,
		stamped?: boolean,
		createdAfter?: Date,
		createdBefore?: Date,
		stampedAfter?: Date,
		stampedBefore?: Date
	) {
		const address = this.apiAddress.ALL_FORMJ;

		const params: Record<string, any> = {};
		if (scheme != undefined) params.scheme = scheme;
		if (hasLoanGroup != undefined) params.hasLoanGroup = hasLoanGroup;
		if (businessId != undefined) params.businessId = businessId;
		if (branchId != undefined) params.branchId = branchId;
		if (licenseId != undefined) params.licenseId = licenseId;
		if (stamped != undefined) params.stamped = stamped;
		if (createdAfter != undefined) {
			createdAfter.setHours(0, 0, 0, 0);
			params.createdAfter = createdAfter.toISOString();
		}
		if (createdBefore != undefined) {
			params.createdBefore = this.sharedService.getLastMsOfDate(createdBefore).toISOString();
		}
		if (stampedAfter != undefined) {
			stampedAfter.setHours(0, 0, 0, 0);
			params.stampedAfter = stampedAfter.toISOString();
		}
		if (stampedBefore != undefined) {
			params.stampedBefore = this.sharedService.getLastMsOfDate(stampedBefore).toISOString();
		}

		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}

	getAddFormJ() {
		return this.apiAddress.FORMJ;
	}

	getUnstampFormJ(formJId: number) {
		return this.apiAddress.FORMJ + '/' + formJId + '/unstamp';
	}

	getDeleteFormJ(id: number) {
		return this.apiAddress.FORMJ + '/' + id;
	}

	getAddAdminRemark(customerID: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/remark';
	}

	getFormJDetails(formJID: number) {
		return this.apiAddress.FORMJ + '/' + formJID;
	}

	getCustomerFormJs(customerID: number, hasLoanGroup?: boolean, scheme?: string) {
		const address = `${this.apiAddress.CUSTOMER}/${customerID}/${this.apiAddress.ALL_FORMJ}`;

		const params: Record<string, any> = {};
		if (hasLoanGroup != undefined) params.hasLoanGroup = hasLoanGroup;
		if (scheme != undefined) params.scheme = scheme;

		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}

	getCutsomerFormJsHasFiles(companyID: number, customerId: number) {
		return this.apiAddress.COMPANY + '/' + companyID + '/customer/' + customerId + '/formJs/hasFiles';
	}

	getCompanyFormJLatestDate(companyID: number) {
		return this.apiAddress.COMPANY + '/' + companyID + '/latestFormJs';
	}

	getCustomerStampedFormJFile(customerId: number, formJId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/${this.apiAddress.FORMJ}/${formJId}/file/stamped`;
	}

	getCustomerStampedFormJFiles(customerId: number, formJId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/${this.apiAddress.FORMJ}/${formJId}/files/stamped`;
	}

	getStampFormJ(formJId: number) {
		return `${this.apiAddress.FORMJ}/${formJId}/stamp`;
	}
	// #endregion

	getCustomerAdminRemarks(customerID: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/remarks';
	}

	getCustomerProfile(customerID: number, profileID: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/profile/' + profileID;
	}

	getProfile(profileID: number) {
		return this.apiAddress.PROFILE + '/' + profileID;
	}

	getCompanyCustomerProfile(companyId: number, customerId: number, profileID: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/customer/' + customerId + '/profile/' + profileID;
	}

	getAllProfiles(profileIDs: number[]) {
		let address = this.apiAddress.ALL_PROFILES + '?';

		for (let i = 0; i < profileIDs.length; i++) {
			address += 'id=' + profileIDs[i] + '&';
		}
		return address;
	}

	getAllCustomersProfiles() {
		return this.apiAddress.ALL_CUSTOMERS + '/profiles';
	}

	getAllUsageStats(refresh: boolean) {
		let address = this.apiAddress.ALL_STATS + '/usage';

		if (refresh == true) address += '?refresh=' + refresh;

		return address;
	}

	getCompaniesLatestCheckIns(companyId?: number, endDate?: string, roleId?: number) {
		let address = this.apiAddress.ALL_COMPANIES + '/checkIns/latest';

		if (companyId != undefined || endDate != undefined || roleId != undefined) address += '?';

		if (companyId != undefined) address += 'companyId=' + companyId;
		if (endDate != undefined) address += '&endDate=' + endDate;
		if (roleId != undefined) address += '&roleId=' + roleId;

		return address;
	}

	getAddCompanyCheckIn(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/checkIn';
	}

	getCompanyCheckInHistory(companyId: number, roleId?: number) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/checkIns';

		if (roleId != undefined) address += '?roleId=' + roleId;

		return address;
	}

	getCompanyDeletedCheckInHistory(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/checkIns/deleted';
	}

	getTokenRefresh() {
		return this.apiAddress.REFRESH_TOKEN;
	}
	getChangeStatus(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/status';
	}

	getMergeProfiles() {
		return this.apiAddress.MERGE_PROFILES;
	}

	getMoveProfile(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/move';
	}

	getForceMoveProfile(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/move/force';
	}

	getEditUserRole(companyId: number, username: string) {
		return this.apiAddress.COMPANY + '/' + companyId + '/user/' + username + '/role';
	}

	getCompanyDeactivateUser(companyId: number, username: string) {
		return this.apiAddress.COMPANY + '/' + companyId + '/user/' + username + '/deactivate';
	}

	getDeactivateUser(username: string) {
		return this.apiAddress.USER + '/' + username + '/deactivate';
	}

	getOnMaintenanceMode() {
		return this.apiAddress.MAINTENANCE_ON;
	}

	getOffMaintenanceMode() {
		return this.apiAddress.MAINTENANCE_OFF;
	}

	getUseStandbyStorage(isOn: boolean) {
		return isOn ? this.apiAddress.USE_STANDBY_STORAGE + '/true' : this.apiAddress.USE_STANDBY_STORAGE + '/false';
	}

	getUseReadOnlyStorage(isOn: boolean) {
		return isOn ? this.apiAddress.USE_READONLY_DB + '/true' : this.apiAddress.USE_READONLY_DB + '/false';
	}

	getCheckCustomerExistence(customerIC: string, customerCode: string) {
		return this.apiAddress.CHCECK_CUSTOMER_CODE + '/' + customerIC + '/' + customerCode;
	}

	getCustomerContactTypedFileExistence(customerId: number, contactId: number, type: string) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/contact/${contactId}/files/${type}/exists`;
	}

	getCustomerContactGuarantorFormExistence(customerId: number, contactId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/files/guarantorForm/exists';
	}

	getCustomerContactPhotoExistence(customerId: number, contactId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/files/photo/exists';
	}

	getCompanyCustomerApplicationFormExistence(companyId: number, customerId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/customer/' + customerId + '/files/application/exists';
	}

	getCompanyCustomerSignedFormJExistence(companyId: number, customerId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/customer/' + customerId + '/files/formj/exists';
	}

	getCompanyCustomerEPFStatementExistence(companyId: number, customerId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/customer/' + customerId + '/files/epfStatement/exists';
	}

	getCustomerTypedFileExistence(customerId: number, type: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/files/' + type + '/exists';
	}

	getCustomerCustomerLoanTypedFileExistence(customerId: number, loanId: number, type: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/loan/' + loanId + '/files/' + type + '/exists';
	}

	getCustomerLoansWithFiles(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/loans/hasFiles';
	}

	getUpdateEPFDetails(customerID: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/epf';
	}

	getUpdateDOB(customerID: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/dob';
	}

	getCompanyOverdueEPFCustomer(companyID: number, endDate?: string) {
		let address = this.apiAddress.COMPANY + '/' + companyID + '/report/overdueEPFCustomers';

		if (endDate != undefined) address += '?date=' + endDate;
		return address;
	}

	getAddLoanRequest() {
		return this.apiAddress.LOAN_APPROVAL_REQUEST;
	}

	getAddElevatedEPFLoanRequest() {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/elevated';
	}

	getUpdateEPFRequestRemark(id: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/' + id + '/remark';
	}

	getUpdateEPFLoanRequest(id: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/' + id;
	}

	getProfileLoanApprovalRequest(profileId: number, approved?: boolean, withLoan?: boolean) {
		const address = `${this.apiAddress.PROFILE}/${profileId}/${this.apiAddress.ALL_LOAN_APPROVAL_REQUEST}`;

		const params: Record<string, any> = {};
		if (approved !== undefined) params.approved = approved;
		if (withLoan !== undefined) params.withLoan = withLoan;

		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}

	getCustomerEPF(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/epf';
	}

	getAddApprovedLoanToAccount(requestId: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/' + requestId + '/loan';
	}

	getLoanApprovalRequestById(id: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/' + id;
	}
	getLoanApprovalRequestByIds(ids: number[]) {
		return this.apiAddress.ALL_LOAN_APPROVAL_REQUEST + '?id=' + ids.join('&id=');
	}
	getAllSharedLoanApprovalRequest(approved?: boolean, myProcessed?: boolean, processed?: boolean) {
		let address = this.apiAddress.ALL_SHARED_LOAN_APPROVAL_AGREEMENT;
		if (approved != undefined || myProcessed != undefined || processed != undefined) address += '?';
		if (approved != undefined) address += 'approved=' + approved + '&';

		if (myProcessed != undefined) address += 'myProcessed=' + myProcessed + '&';
		if (processed != undefined) address += 'processed=' + processed + '&';

		return address;
	}

	getAllLoanApprovalRequest(
		endDate?: string,
		withLoan?: boolean,
		approved?: boolean,
		includeUnprocessed?: boolean,
		authorized?: boolean,
		startDate?: string,
		customerId?: number,
		showUninvolved?: boolean
	) {
		let address = this.apiAddress.ALL_LOAN_APPROVAL_REQUEST;
		if (endDate != undefined || withLoan != undefined || approved != undefined || authorized != undefined || startDate != undefined || customerId != undefined) address += '?';

		if (endDate != undefined) address += 'endDate=' + endDate + '&';

		if (withLoan != undefined) address += 'withLoan=' + withLoan + '&';

		if (approved != undefined) {
			address += 'approved=' + approved + '&';
		}

		if (includeUnprocessed != undefined) {
			address += 'inclUnprocessed=' + includeUnprocessed + '&';
		}
		if (authorized != undefined) {
			address += 'authorized=' + authorized + '&';
		}
		if (startDate != undefined) {
			address += 'startDate=' + startDate + '&';
		}
		if (customerId != undefined) {
			address += 'customerId=' + customerId + '&';
		}

		if (showUninvolved != undefined) {
			address += 'showUninvolved=' + showUninvolved + '&';
		}
		return address;
	}

	getApproveLoanApprovalRequest(id: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/' + id + '/approve';
	}

	getDenyLoanApprovalRequest(id: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/' + id + '/deny';
	}

	getApproveGroupLoanApprovalRequest(groupId: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/group/' + groupId + '/approve';
	}

	getDenyGroupLoanApprovalRequest(groupId: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/group/' + groupId + '/deny';
	}

	getAuthorizeLoanApprovalRequest(id: number) {
		return this.apiAddress.LOAN_APPROVAL_REQUEST + '/' + id + '/authorize';
	}

	getMarkProfileTaskAsDone(profileId: number, taskId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/task/' + taskId + '/done';
	}

	getMarkLegacyEPF() {
		return this.apiAddress.ALL_LOANS + '/EPF';
	}

	getAddLegacyEPFSettlement(id: number) {
		return this.apiAddress.LOAN + '/' + id + '/legacySettlement';
	}

	getCompanyCustomerReport(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/report/customerRecord';
	}

	getServerStatus() {
		return this.apiAddress.SERVER + '/status';
	}

	getAuthLog(username?: string, ip?: string, userAgent?: string, reason?: string, success?: boolean, startDate?: string, endDate?: string) {
		let address = this.apiAddress.AUTH_LOG;

		const params: Record<string, any> = {};
		if (startDate !== undefined) params.startDate = startDate;
		if (endDate !== undefined) params.endDate = endDate;
		if (username !== undefined) params.username = username;
		if (reason !== undefined) params.reason = reason;
		if (userAgent !== undefined) params.userAgent = userAgent;
		if (success !== undefined) params.success = success;
		if (ip !== undefined) params.ip = ip;

		const paramsString = new URLSearchParams(params).toString();
		address = [address, paramsString].join('?');
		return address;
	}

	getErrorLog(code?: string) {
		let address = this.apiAddress.SERVER + '/log/errors';

		if (code != undefined) address += '?code=' + code;
		return address;
	}

	getSignedInUserList(activeWithin?: string) {
		let address = this.apiAddress.SIGNED_IN_USERS;

		if (activeWithin != undefined) address += '?activeWithin=' + activeWithin;

		return address;
	}

	getCompanyAllSets(
		companyId: number,
		startDate?: string,
		endDate?: string,
		valid?: boolean,
		checked?: boolean,
		pending?: boolean,
		includePending?: boolean,
		type?: number,
		subtype?: number,
		saved?: boolean
	) {
		const address = this.apiAddress.COMPANY + '/' + companyId + '/sets';

		const params: Record<string, any> = {};
		if (startDate !== undefined) params.startDate = startDate;
		if (endDate !== undefined) params.endDate = endDate;
		if (valid !== undefined) params.valid = valid;
		if (checked !== undefined) params.checked = checked;
		if (pending !== undefined) params.pending = pending;
		if (includePending !== undefined) params.inclPending = includePending;
		if (type !== undefined && type != 0) params.type = type;
		if (subtype !== undefined && subtype != 0) params.subtype = subtype;
		if (saved !== undefined && saved !== false) params.saved = saved;

		const paramsString = new URLSearchParams(params).toString();
		return [address, paramsString].join('?');
	}

	getAllSets(
		startDate?: string,
		endDate?: string,
		valid?: boolean,
		checked?: boolean,
		pending?: boolean,
		includePending?: boolean,
		type?: number,
		priority?: number,
		subtype?: number,
		saved?: boolean,
		limit?: number,
		sortASC?: boolean
	) {
		const address = this.apiAddress.ALL_SETS;

		const params: Record<string, any> = {};
		if (startDate !== undefined) params.startDate = startDate;
		if (endDate !== undefined) params.endDate = endDate;
		if (valid !== undefined) params.valid = valid;
		if (checked !== undefined) params.checked = checked;
		if (pending !== undefined) params.pending = pending;
		if (includePending !== undefined) params.inclPending = includePending;
		if (type !== undefined && type != 0) params.type = type;
		if (priority !== undefined) params.priority = priority;
		if (subtype !== undefined && subtype != 0) params.subtype = subtype;
		if (saved !== undefined && saved !== false) params.saved = saved;
		if (limit !== undefined) params.limit = limit;
		if (sortASC !== undefined) params.sortOrder = sortASC ? 'ASC' : 'DESC';

		const paramsString = new URLSearchParams(params).toString();
		return [address, paramsString].join('?');
	}

	getAddOrRemoveCompanySetTransactions(companyId: number, setId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/set/' + setId + '/transactions';
	}

	getAddNewCompanySet(companyId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/set';
	}

	getSubmitSet(companyId: number, setId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/set/' + setId + '/submit';
	}

	getValidateSet(companyId: number, setId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/set/' + setId + '/validate';
	}

	getInvalidateSet(companyId: number, setId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/set/' + setId + '/invalidate';
	}

	getEditSet(setId: number) {
		return this.apiAddress.SET + '/' + setId;
	}

	getUploadSetFile(setId: number) {
		return this.apiAddress.SET + '/' + setId + '/file';
	}

	getUploadBlacklistFile(id: number) {
		return this.apiAddress.BLACKLIST + '/' + id + '/file';
	}

	getAllSetFiles(setId: number) {
		return this.apiAddress.SET + '/' + setId + '/files';
	}

	getSetFileFull(setId: number, filename: string) {
		return this.apiAddress.SET + '/' + setId + '/file/' + filename;
	}

	getSingleSetDetails(setId: number) {
		return this.apiAddress.SET + '/' + setId;
	}

	getSingleLoanArrear(loanId: number, arrearId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/arrear/' + arrearId;
	}

	getMoveLoan(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/move';
	}

	getForceMoveLoan(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/move/force';
	}

	getAddAdvanceBorrow() {
		return this.apiAddress.ADVANCE + '/borrow';
	}
	getAddAdvanceDeposit() {
		return this.apiAddress.ADVANCE + '/deposit';
	}

	getAddAdvanceReturn(advanceId: number) {
		return this.apiAddress.ADVANCE + '/' + advanceId + '/return';
	}

	getAddAdvanceWithdraw(advanceId: number) {
		return this.apiAddress.ADVANCE + '/' + advanceId + '/withdrawal';
	}

	getAddAdvanceDepositMore(advanceId: number) {
		return this.apiAddress.ADVANCE + '/' + advanceId + '/deposit';
	}

	getAddAdvanceBorrowMore(advanceId: number) {
		return this.apiAddress.ADVANCE + '/' + advanceId + '/borrow';
	}

	getTypedTransaction(receipts: string, transactionId: number) {
		//receiptType: loans, badDebts, reserves, assets, advances

		return receipts + '/receipts/transaction/' + transactionId;
	}

	getSetsCount(
		pending?: boolean,
		valid?: boolean,
		includePending?: boolean,
		type?: number,
		subtype?: number,
		companyId?: number,
		startDate?: string,
		endDate?: string,
		priority?: number,
		maxPriority?: number,
		minPriority?: number,
		checked?: boolean
	) {
		let address = companyId == undefined ? this.apiAddress.ALL_SETS + '/count' : this.apiAddress.COMPANY + '/' + companyId + '/' + this.apiAddress.ALL_SETS + '/count';

		const params: Record<string, any> = {};
		if (pending !== undefined) params.pending = pending;
		if (valid !== undefined) params.valid = valid;
		if (includePending !== undefined) params.inclPending = includePending;
		if (type !== undefined && type !== 0) params.type = type;
		if (subtype !== undefined && subtype !== 0) params.subtype = subtype;
		// if (companyId !== undefined) params.companyId = companyId;
		if (startDate !== undefined) params.startDate = startDate;
		if (endDate !== undefined) params.endDate = endDate;
		if (priority !== undefined) params.priority = priority;
		if (maxPriority !== undefined) params.maxPriority = maxPriority;
		if (minPriority !== undefined) params.minPriority = minPriority;
		if (checked !== undefined) params.checked = checked;

		const paramsString = new URLSearchParams(params).toString();
		address = [address, paramsString].join('?');

		return address;
	}

	getCustomerCount(verified?: boolean, status?: string, priority?: number, minPriority?: number, maxPriority?: number) {
		let address = this.apiAddress.ALL_CUSTOMERS + '/count';

		if (verified != undefined || status != undefined || priority != undefined || minPriority != undefined || maxPriority != undefined) address += '?';

		if (verified != undefined) address += 'verified=' + verified;
		if (status != undefined) address += 'status=' + status;
		if (priority != undefined) address += 'priority=' + priority;
		if (minPriority != undefined) address += 'minPriority=' + minPriority;
		if (maxPriority != undefined) address += 'maxPriority=' + maxPriority;

		return address;
	}

	getCompCustomerCount(companyId: number, verified?: boolean, status?: string, priority?: number, minPriority?: number, maxPriority?: number) {
		let address = `${this.apiAddress.COMPANY}/${companyId}/${this.apiAddress.ALL_CUSTOMERS}/count`;

		const params: Record<string, any> = {};
		if (verified != undefined) params.verified = verified;
		if (status != undefined) params.status = status;
		if (priority != undefined) params.priority = priority;
		if (minPriority != undefined) params.minPriority = minPriority;
		if (maxPriority != undefined) params.maxPriority = maxPriority;

		const paramsString = new URLSearchParams(params).toString();
		address = [address, paramsString].join('?');

		return address;
	}

	getVerifyCustomer(customerID: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/verify';
	}

	getUnverifyCustomer(customerID: number) {
		return this.apiAddress.CUSTOMER + '/' + customerID + '/unverify';
	}

	getChangeFileType(customerId: number, filename: string, targetType: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/files/' + targetType + '/' + filename;
	}

	getChangeContactFileType(customerId: number, contactId: number, filename: string, targetType: string) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/contact/' + contactId + '/files/' + targetType + '/' + filename;
	}

	getWebsiteReleases() {
		return this.apiAddress.WEBAPP_RELEASES;
	}

	getUpdateWebAppRelease(tag: string, filename: string) {
		return this.apiAddress.WEBAPP_RELEASES + '/' + tag + '/' + filename;
	}

	getCompaniesAccess(companyId?: number) {
		let address = this.apiAddress.ALL_USERS + '/companies';

		if (companyId != undefined) address += '?companyId=' + companyId;

		return address;
	}

	getGrantUserCompanyAccess(username: string, priority: number) {
		return this.apiAddress.USER + '/' + username + '/companies/' + priority;
	}

	getRevokeUserCompanyAccess(username: string) {
		return this.apiAddress.USER + '/' + username + '/companies';
	}

	getSettledLoansSettlementInfos() {
		return this.apiAddress.SETTLED_LOAN_SETTLEMENT_INFOS;
	}

	getCheckLoanSettlementInfo(loanId: number) {
		return this.apiAddress.SETTLED_LOAN_SETTLEMENT_INFO + '/' + loanId + '/check';
	}

	getUnheckLoanSettlementInfo(loanId: number) {
		return this.apiAddress.SETTLED_LOAN_SETTLEMENT_INFO + '/' + loanId + '/uncheck';
	}

	getAddSetRemark(companyId: number, setId: number) {
		return this.apiAddress.COMPANY + '/' + companyId + '/set/' + setId + '/remark';
	}

	getEditTransactionSubtype(transactionId: number) {
		return this.apiAddress.TRANSACTION + '/' + transactionId + '/subtype';
	}

	getSearchCPCustomer(name?: string, icNumber?: string, code?: string) {
		let address = this.apiAddress.CP_SEARCH;

		if (name != undefined || icNumber != undefined || code != undefined) address += '?';

		if (name != undefined && name != '') address += 'name=' + this.sharedService.processStringStripInvalidSymbol(name) + '&';
		if (icNumber != undefined && icNumber != '') address += 'icNumber=' + this.sharedService.processStringStripInvalidSymbol(icNumber) + '&';
		if (code != undefined && code != '') address += 'code=' + this.sharedService.processStringStripInvalidSymbol(code) + '&';

		return address;
	}

	getSearchGuarantor(icNumber: string, excludeCustomerId?: string, companyId?: string) {
		let address = this.apiAddress.ALL_CONTACTS + '/guarantors/search';

		if (excludeCustomerId != undefined || icNumber != undefined || companyId != undefined) address += '?';

		if (excludeCustomerId != undefined && excludeCustomerId != '') address += 'excludeCustomerId=' + this.sharedService.processStringStripInvalidSymbol(excludeCustomerId) + '&';
		if (icNumber != undefined && icNumber != '') address += 'icNumber=' + this.sharedService.processStringStripInvalidSymbol(icNumber) + '&';
		if (companyId != undefined && companyId != '') address += 'companyId=' + this.sharedService.processStringStripInvalidSymbol(companyId) + '&';

		return address;
	}

	getSearchConflictedTicketCustomer(icNumber: string, excludeCustomerId?: number, approved?: boolean, inclUnprocessed?: boolean, withLoan?: false) {
		let address = this.apiAddress.ALL_LOAN_APPROVAL_REQUEST;

		if (excludeCustomerId != undefined || icNumber != undefined || approved != undefined || inclUnprocessed != undefined || withLoan != undefined) address += '?';

		if (excludeCustomerId != undefined) address += 'exclCustomerId=' + excludeCustomerId + '&';
		if (icNumber != undefined && icNumber != '') address += 'icNumber=' + icNumber + '&';
		if (approved != undefined) address += 'approved=' + approved + '&';
		if (inclUnprocessed != undefined) address += 'inclUnprocessed=' + inclUnprocessed + '&';
		if (withLoan != undefined) address += 'withLoan=' + withLoan + '&';

		return address;
	}

	getCPCustomerDetails(customerId: number) {
		return this.apiAddress.CP_CUSTOMER + '/' + customerId;
	}

	getCPCustomerRemarks(customerId: number) {
		return this.apiAddress.CP_CUSTOMER + '/' + customerId + '/remarks';
	}

	getCompanyAllCPCustomer(companyId: number) {
		return this.apiAddress.CP_COMPANY + '/' + companyId + '/customers';
	}

	getCompanyAllCPTransactions(companyId: number, confirmed?: boolean, startDate?: string, endDate?: string, year?: number) {
		let address = this.apiAddress.CP_COMPANY + '/' + companyId + '/transactions';
		if (confirmed != undefined || startDate != undefined || endDate != undefined || year != undefined) address += '?';

		if (confirmed != undefined) address += 'confirmed=' + confirmed + '&';
		if (startDate != undefined) address += 'startDate=' + startDate + '&';
		if (endDate != undefined) address += 'endDate=' + endDate + '&';
		if (year != undefined) address += 'year=' + year;

		return address;
	}

	getAddTransactionRequest() {
		return this.apiAddress.TRANSACTIONS_REQUEST;
	}

	getAllTransactionRequests(hasTransaction?: boolean) {
		let address = this.apiAddress.ALL_TRANSACTION_REQUEST;
		if (hasTransaction != undefined) address += '?hasTransactions=' + hasTransaction;

		return address;
	}

	getAddAssignedTransactionToAccount(requestId: number) {
		return this.apiAddress.TRANSACTIONS_REQUEST + '/' + requestId;
	}

	getDeleteTransactionRequest(requestId: number) {
		return this.apiAddress.TRANSACTIONS_REQUEST + '/' + requestId;
	}

	getCustomerEPFChangeHistory(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/epf/history';
	}

	getCustomerICChangeHistory(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/ic/history';
	}

	getAddPulledCustomerRecord(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/recordPull';
	}

	getAddBlacklistCustomer() {
		return this.apiAddress.BLACKLIST;
	}

	updateBlacklistCustomer(id: number) {
		return this.apiAddress.BLACKLIST + '/' + id;
	}

	getAllBlacklist(icNumber?: string, released?: boolean) {
		let address = this.apiAddress.ALL_BLACKLIST;
		if (icNumber != undefined) address += '?icNumber=' + icNumber;

		if (released != undefined) address += '&released=' + released;

		return address;
	}

	getPatchReleaseCustomer(id: number) {
		return this.apiAddress.BLACKLIST + '/' + id + '/release';
	}

	getValidateEPFStatement() {
		return this.apiAddress.ALL_FILES + '/validateEpfStatement';
	}

	getSharedLoanProcess(id: number, action: string) {
		return this.apiAddress.SHARED_LOAN_APPROVAL_REQUEST + '/' + id + '/' + action;
	}

	getAllReserveGroup(
		profileId?: number,
		companyId?: number,
		accepted?: boolean,
		myAccepted?: boolean,
		startDate?: string,
		endDate?: string,
		valid?: boolean,
		checked?: boolean,
		pending?: boolean,
		includePending?: boolean,
		lastId?: number,
		limit?: number,
		sortASC?: boolean
	) {
		const address = this.apiAddress.ALL_RESERVE_GROUP;

		const params: Record<string, any> = {};
		if (startDate !== undefined) params.startDate = startDate;
		if (endDate !== undefined) params.endDate = endDate;
		if (valid !== undefined) params.valid = valid;
		if (checked !== undefined) params.checked = checked;
		if (pending !== undefined) params.pending = pending;
		if (includePending !== undefined) params.inclPending = includePending;
		if (limit !== undefined) params.limit = limit;
		if (sortASC !== undefined) params.sortOrder = sortASC ? 'ASC' : 'DESC';
		if (profileId !== undefined) params.profileId = profileId;
		if (companyId !== undefined) params.companyId = companyId;
		if (accepted !== undefined) params.accepted = accepted;
		if (myAccepted !== undefined) params.myAccepted = myAccepted;
		if (lastId !== undefined) params.lastId = lastId;

		const paramsString = new URLSearchParams(params).toString();
		return [address, paramsString].join('?');
	}

	getAddProfileReserveGroup(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/reserveGroup';
	}

	getAllProfileReserveGroups(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/reserveGroups';
	}

	getAcceptReserveGroup(rgId: number) {
		return this.apiAddress.RESERVE_GROUP + '/' + rgId + '/accept';
	}

	getSubmitReserveGroup(rgId: number) {
		return this.apiAddress.RESERVE_GROUP + '/' + rgId + '/submit';
	}

	getValidateReserveGroup(rgId: number) {
		return this.apiAddress.RESERVE_GROUP + '/' + rgId + '/validate';
	}

	getInvalidateReserveGroup(rgId: number) {
		return this.apiAddress.RESERVE_GROUP + '/' + rgId + '/invalidate';
	}

	getSharedProfile(profileID: number) {
		return this.apiAddress.SHARED_PROFILE + '/' + profileID;
	}

	getReserveGroupDetails(rgId: number) {
		return this.apiAddress.RESERVE_GROUP + '/' + rgId;
	}

	getCompanyByUID(uid: string) {
		return this.apiAddress.COMPANY + '/uid/' + uid;
	}

	getAddSharedLoanRequest() {
		return this.apiAddress.SHARED_LOAN_APPROVAL_REQUEST;
	}

	getAddSharedElevatedEPFLoanRequest() {
		return this.apiAddress.SHARED_LOAN_APPROVAL_REQUEST + '/elevated';
	}

	getLoanCommissionUsers(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/commissionUsers';
	}

	getCompanyLoanApprovalRequests(companyId: number, loanGroupId?: number) {
		let address = this.apiAddress.COMPANY + '/' + companyId + '/loanApprovalRequests';

		if (loanGroupId != undefined) address += '?loanGroupId=' + loanGroupId;

		return address;
	}

	getLoanGroupApprovalRequests(loanGroupId: number) {
		const address = this.apiAddress.LOAN_GROUP + '/' + loanGroupId + '/loanApprovalRequests';

		return address;
	}

	getShareProfileToAnotherProfile(mainProfileId: number, toProfileId: number) {
		return this.apiAddress.SHARED_PROFILE + '/' + mainProfileId + '/shareWith/' + toProfileId;
	}

	getUnshareProfile(profileIdToUnshare: number) {
		return this.apiAddress.SHARED_PROFILE + '/' + profileIdToUnshare + '/unshare';
	}

	getMarkProfileAsMainShared(customerId: number, profileId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/profile/' + profileId + '/shareAsShared';
	}

	getProfilePossibleSharedProfile(profileId: number) {
		return this.apiAddress.PROFILE + '/' + profileId + '/possibleSharedProfiles';
	}

	getDeleteReserveGroup(rgId: number) {
		return this.apiAddress.RESERVE_GROUP + '/' + rgId;
	}

	getMoveCentsToInterest(loanId: number) {
		return this.apiAddress.LOAN + '/' + loanId + '/moveSettlementChargesCentsToInterest';
	}

	getAddAccountAuthorization(customerId: number, accountId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/account/' + accountId + '/authorization';
	}

	getAllCustomerAccountsAuthorization(customerIds?: number[], contactId?: number, pending?: boolean) {
		let address = this.apiAddress.ALL_CUSTOMERS + '/accounts/authorizations';

		if (customerIds != undefined) {
			address += '?';
			customerIds.forEach(id => {
				address += 'customerId=' + id + '&';
			});
		}
		if (contactId != undefined) address += '?contactId=' + contactId + '&';
		if (pending != undefined) address += '?pending=' + pending;

		return address;
	}

	getAccountAuthorization(accountId: number) {
		return this.apiAddress.ALL_CUSTOMERS + '/account/' + accountId + '/authorizations';
	}

	getAccountAuthorizationById(id: number) {
		return this.apiAddress.ALL_CUSTOMERS + '/accounts/authorization/' + id;
	}

	getGrantAccountAuthorization(id: number) {
		return this.apiAddress.ALL_CUSTOMERS + '/accounts/authorization/' + id + '/grant';
	}

	getRevokeAccountAuthorization(id: number) {
		return this.apiAddress.ALL_CUSTOMERS + '/accounts/authorization/' + id + '/revoke';
	}

	getLoanGroupById(id: number, showUninvolved?: boolean) {
		var address = this.apiAddress.LOAN_GROUP + '/' + id;
		if (showUninvolved != undefined) address += '?showUninvolved';
		return address;
	}

	getLoanGroups(ids?: number[], hasOverrideCommissionRateLimit?: boolean, showUninvolved?: boolean) {
		let address = this.apiAddress.ALL_LOAN_GROUP;
		const p: Record<string, any> = {};
		if (hasOverrideCommissionRateLimit !== undefined) p.hasOverrideCommissionRateLimit = hasOverrideCommissionRateLimit;
		if (showUninvolved !== undefined) p.showUninvolved = showUninvolved;

		const params = new URLSearchParams(p);
		if (ids != undefined) ids.forEach(id => params.append('id', id.toString()));

		const paramsString = new URLSearchParams(params).toString();
		address = [address, paramsString].join('?');
		return address;
	}

	getOverrideLoanGroupCommLimit(id: number) {
		return this.apiAddress.LOAN_GROUP + '/' + id + '/commissionRateLimit/override';
	}

	getCustomerFilesArchive(customerId: number, cp?: boolean) {
		const address = this.apiAddress.ARCHIVE_FILE + '?customerId=' + customerId;
		return cp ? address + '&cp' : address;
	}

	// #region 'Customer Account'
	getAddCustomerAccount(customerId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/account';
	}

	getAllCustomerAccount(customerIds: number[]) {
		let address = this.apiAddress.ALL_CUSTOMERS + '/accounts';

		if (customerIds != undefined) {
			address += '?';
			customerIds.forEach(id => {
				address += 'customerId=' + id + '&';
			});
		}

		return address;
	}

	getBankAccountById(acctId: number) {
		return this.apiAddress.ALL_CUSTOMERS + '/account/' + acctId;
	}

	getBankAccounts(acctIds?: number[]) {
		let address = this.apiAddress.ALL_CUSTOMERS + '/accounts';
		if (acctIds != undefined) {
			address += '?';
			acctIds.forEach(id => {
				address += 'id=' + id + '&';
			});
		}
		return address;
	}

	getCustomerAccountById(customerId: number, acctId: number) {
		return this.apiAddress.CUSTOMER + '/' + customerId + '/account/' + acctId;
	}

	getCustomerAccountDisable(customerId: number, accountId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/account/${accountId}/disable`;
	}
	// #endregion

	// #region 'Business, Branch & License'
	getAllBusinesses(companyId?: number, ssmExpired?: boolean) {
		const address = this.apiAddress.ALL_BUSINESSES;
		const params: Record<string, any> = {};
		if (companyId !== undefined) params.companyId = companyId;
		if (ssmExpired !== undefined) params.ssmExpired = ssmExpired;
		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}

	getBusiness() {
		return this.apiAddress.BUSINESS;
	}

	getBusinessById(id: number) {
		return `${this.apiAddress.BUSINESS}/${id}`;
	}

	getBusinessToggleRequireConsentForm(businessId: number) {
		return `${this.apiAddress.BUSINESS}/${businessId}/toggleConsentFormRequirement`;
	}

	getAllBusinessBranches(businessId?: number, companyId?: number) {
		const address = this.apiAddress.ALL_BUSINESS_BRANCHES;
		const params: Record<string, any> = {};
		if (businessId !== undefined) params.businessId = businessId;
		if (companyId !== undefined) params.companyId = companyId;
		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}

	getBusinessBranch() {
		return this.apiAddress.BUSINESS_BRANCH;
	}

	getBusinessBranchLicense() {
		return `${this.apiAddress.BUSINESS_BRANCH}/license`;
	}

	getBusinessBranchById(id: number) {
		return `${this.apiAddress.BUSINESS_BRANCH}/${id}`;
	}

	getAllLicenses(active?: boolean, expired?: boolean, invalidated?: boolean, businessId?: number, companyId?: number) {
		const address = this.apiAddress.ALL_LICENSES;
		const params: Record<string, any> = {};
		if (active !== undefined) params.active = active;
		if (expired !== undefined) params.expired = expired;
		if (invalidated !== undefined) params.invalidated = invalidated;
		if (businessId !== undefined) params.businessId = businessId;
		if (companyId !== undefined) params.companyId = companyId;
		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}

	getLicense() {
		return this.apiAddress.LICENSE;
	}

	getLicenseById(id: number) {
		return `${this.apiAddress.LICENSE}/${id}`;
	}

	getLatestLicenseByCompanyId(companyId: number) {
		return `${this.apiAddress.COMPANY}/${companyId}/${this.apiAddress.LICENSE}/latest`;
	}

	getLatestLicenseByCustomerId(customerId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/${this.apiAddress.LICENSE}/latest`;
	}

	getInvalidateLicense(id: number) {
		return `${this.apiAddress.LICENSE}/${id}/invalidate`;
	}

	getRevalidateLicense(id: number) {
		return `${this.apiAddress.LICENSE}/${id}/revalidate`;
	}
	// #endregion

	// #region 'fiscal year info'
	getFiscalYearInfos(branchId?: number | number[], fiscalYear?: number | number[]) {
		const url = `${this.apiAddress.ALL_BUSINESS_BRANCHES}/${this.apiAddress.ALL_FISCAL_YEAR_TARGETS}`;
		branchId = [branchId ?? []].flat();
		fiscalYear = [fiscalYear ?? []].flat();

		const params = new URLSearchParams();
		if (branchId.length > 0) branchId.forEach(b => params.append('businessBranchId', b.toString()));
		if (fiscalYear.length > 0) fiscalYear.forEach(f => params.append('fiscalYear', f.toString()));

		return params.toString().length > 0 ? [url, params.toString()].join('?') : url;
	}

	getBusinessBranchAllFiscalYearInfos(branchId: number) {
		return `${this.apiAddress.BUSINESS_BRANCH}/${branchId}/${this.apiAddress.ALL_FISCAL_YEAR_TARGETS}`;
	}

	getBusinessBranchFiscalYearInfo(branchId: number, year: number) {
		return `${this.apiAddress.BUSINESS_BRANCH}/${branchId}/${this.apiAddress.FISCAL_YEAR_TARGET}/${year}`;
	}

	// #endregion

	// #region 'e-invoice'
	getAllEInvoiceDocs(businessId?: number, licenseId?: number, issuedAfter?: Date, issuedBefore?: Date, submittedAfter?: Date, submittedBefore?: Date, submitted?: boolean) {
		const address = this.apiAddress.ALL_EINVOICE_DOCS;

		const params: Record<string, any> = {};
		if (businessId !== undefined) params.businessId = businessId;
		if (licenseId !== undefined) params.businessLicenseId = licenseId;
		if (issuedAfter !== undefined) params.issuedAfter = issuedAfter.toISOString();
		if (issuedBefore !== undefined) params.issuedBefore = issuedBefore.toISOString();
		if (submittedAfter !== undefined) params.submittedAfter = submittedAfter.toISOString();
		if (submittedBefore !== undefined) params.submittedBefore = submittedBefore.toISOString();
		if (submitted !== undefined) params.submitted = submitted;

		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}

	getEInvoiceDocById(id: number) {
		return `${this.apiAddress.EINVOICE_DOC}/${id}`;
	}

	getSubmitEInvoiceDocs() {
		return `${this.apiAddress.ALL_EINVOICE_DOCS}/submit`;
	}

	getEInvoiceExcel() {
		return `${this.apiAddress.ALL_EINVOICE_DOCS}/BatchSummissionExcel`;
	}
	// #endregion

	// #region 'Sheet Request'
	getPostSheetRequest() {
		return this.apiAddress.SHEET_REQUEST;
	}

	getAllSheetRequests(companyId?: number, requestedBefore?: string, requestedAfter?: string, requestedBy?: string) {
		const address = this.apiAddress.ALL_SHEET_REQUESTS;
		const params: Record<string, any> = {};
		if (companyId != undefined) params.companyId = companyId;
		if (requestedBefore != undefined) params.requestedBefore = requestedBefore;
		if (requestedAfter != undefined) params.requestedAfter = requestedAfter;
		if (requestedBy != undefined) params.businessId = requestedBy;

		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}
	// #endregion
	// #region 'SQL INFO'

	getSqlInfoOfSingleCompany(companyId: number) {
		return `${this.apiAddress.COMPANY}/${companyId}/sqlInfo`;
	}
	// #endregion

	// #region 'Finance Account'
	getAllFinanceAccount(id?: number, code?: string, disabled?: boolean, businessId?: number) {
		const address = this.apiAddress.ALL_FINANCE_ACCOUNTS;

		const params: Record<string, any> = {};
		if (id != undefined) params.id = id;
		if (code != undefined) params.code = code;
		if (disabled != undefined) params.disabled = disabled;
		if (businessId != undefined) params.businessId = businessId;

		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}
	getBusinessFinanceAccounts(businessId: number) {
		return `${this.apiAddress.BUSINESS}/${businessId}/financeAccounts`;
	}

	getAddFinanceAccount() {
		return `${this.apiAddress.FINANCE_ACCOUNT}`;
	}

	getFinanceAccountById(id: number) {
		return `${this.apiAddress.FINANCE_ACCOUNT}/${id}`;
	}

	getDisableFinanceAccount(faId: number) {
		return `${this.apiAddress.FINANCE_ACCOUNT}/${faId}/disable`;
	}

	getLinkBusinessToFinanceAccount(faId: number, businessId: number) {
		return `${this.apiAddress.BUSINESS}/${businessId}/${this.apiAddress.FINANCE_ACCOUNT}/${faId}/link`;
	}

	getUnlinkBusinessToFinanceAccount(faId: number, businessId: number) {
		return `${this.apiAddress.BUSINESS}/${businessId}/${this.apiAddress.FINANCE_ACCOUNT}/${faId}/unlink`;
	}
	// #endregion

	// #region 'Docuseal'
	getFormJDocuseal(id: number, omitDate?: boolean) {
		const address = `${this.apiAddress.FORMJ}/${id}/docuseal`;

		const params: Record<string, any> = {};
		if (omitDate != undefined) params.omitDate = omitDate;
		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}
	getCustomerFormJDocuseals(customerId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/formJs/docuseals`;
	}

	getPostGuarantorDocuseal(customerId: number, contactId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/contact/${contactId}/guarantorForm/docuseal`;
	}

	getCustomerGuarantorDocuseals(customerId: number, contactId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/contact/${contactId}/guarantorForm/docuseals`;
	}

	getGuarantorDocusealDetails(customerId: number, contactId: number, id: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/contact/${contactId}/guarantorForm/docuseal/${id}`;
	}

	getPostSettlementBreakdownDocuseal(customerId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/docuseal/settlementBreakdown`;
	}

	getPostAccountAuthorizationDocuseal(customerId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/docuseal/accountAuthorizationLetter`;
	}

	getCustomerDocuseals(customerId: number, type?: string) {
		const address = `${this.apiAddress.CUSTOMER}/${customerId}/docuseals`;
		const params: Record<string, any> = {};
		if (type != undefined) params.type = type;

		const paramsString = new URLSearchParams(params).toString();
		return paramsString.length > 0 ? [address, paramsString].join('?') : address;
	}

	getCustomerDocusealDetails(customerId: number, docusealId: number) {
		return `${this.apiAddress.CUSTOMER}/${customerId}/docuseal/${docusealId}`;
	}
	// #endregion
}
